<template>
  <div
    class="min-w-160 space-y-5 rounded-md bg-white pt-5 shadow-lg"
    data-cy="min-and-max-filter"
  >
    <slot name="header">
      <lf-h3 class="px-5">
        <span v-tooltip="{ content: subTitle, onShow: () => !!subTitle }">
          {{ label }}
          <span v-if="unit" class="text-sm text-gray-400">
            ({{ $t("COMMON.IN") }} {{ unitLabel }})
          </span>
        </span>
      </lf-h3>
    </slot>
    <min-and-max-input
      v-model:min="min"
      v-model:max="max"
      class="px-5"
      :max-options="{ 'data-maska': mask }"
      :min-options="{ 'data-maska': mask }"
    />
    <div
      class="sticky bottom-0 flex h-11 items-center justify-evenly border-t px-4 py-0"
    >
      <lf-button
        v-if="showClearFilter"
        class="transform cursor-pointer text-sm font-medium text-primary active:scale-95"
        :disabled="!modelValue.length"
        @click="clearFilter"
      >
        {{ $t("COMMON.CLEAR_FILTER") }}
      </lf-button>
      <lf-button
        class="transform cursor-pointer text-sm font-medium text-primary active:scale-95"
        :disabled="!canApply"
        @click="applyFilter"
      >
        {{ $t("COMMON.APPLY") }}
      </lf-button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { deepEqual } from "fast-equals";

import { Unit } from "@/enums/datetime";

import LfButton from "@/components/ui/buttons/LfButton.vue";
import MinAndMaxInput from "@/components/ui/inputs/MinAndMax.vue";

interface Props {
  label: string;
  modelValue: number[];
  showClearFilter: boolean;
  unit?: Unit;
  mask?: string;
  subTitle?: string;
  allowMaxZero?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showClearFilter: false,
  mask: "####",
  allowMaxZero: false,
});

const emit = defineEmits<{
  change: [value: number[]];
}>();

const { t } = useI18n();

const min = ref<number | null>(props.modelValue[0] ?? null);
const max = ref<number | null>(props.modelValue[1] ?? null);

const unitLabel = computed(() => {
  switch (props.unit) {
    case Unit.Dollar:
      return t("COMMON.DOLLAR_SIGN");
    case Unit.Minutes:
      return t("COMMON.MINUTE", 2);
    case Unit.Months:
      return t("COMMON.MONTHS");
    case Unit.Points:
      return t("COMMON.POINT", 2);
    default:
      return t("COMMON.HOUR", 2);
  }
});

const canApply = computed(() => {
  if (deepEqual([min.value, max.value], props.modelValue)) {
    return false;
  }
  if (typeof min.value === "number" && min.value >= 0) {
    if (typeof max.value === "number" && max.value > 0) {
      return min.value <= max.value;
    } else {
      return max.value !== 0 || props.allowMaxZero;
    }
  }

  if (typeof max.value === "number" && max.value > 0) {
    if (typeof min.value === "number" && min.value > 0) {
      return min.value <= max.value;
    } else {
      return !min.value;
    }
  }

  return false;
});

const clearFilter = () => {
  min.value = null;
  max.value = null;
  emit("change", []);
};

const applyFilter = () => {
  if (!canApply.value) {
    return;
  }
  const value = [min.value ?? 0];
  if (max.value || (max.value === 0 && props.allowMaxZero)) {
    value.push(max.value);
  }
  emit("change", value);
};
</script>
